import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, formatApiError } from "../lib/api";
import { useUnits, CREAT_SI_TO_US, CREAT_US_TO_SI } from "../context/UnitsContext";
import { toast } from "sonner";

export default function EditLabDialog({ catId, lab, open, onOpenChange, onSaved }) {
  const { units } = useUnits();
  const creatUnit = units === "SI" ? "µmol/L" : "mg/dL";

  const initial = () => ({
    date: lab?.date || new Date().toISOString().slice(0, 10),
    creatinine:
      lab?.creatinine_mg_dl == null
        ? ""
        : units === "SI"
          ? (lab.creatinine_mg_dl * CREAT_US_TO_SI).toFixed(1)
          : Number(lab.creatinine_mg_dl).toFixed(2),
    sdma: lab?.sdma_ug_dl ?? "",
    usg: lab?.usg ?? "",
    upc: lab?.upc ?? "",
    bp: lab?.bp_systolic ?? "",
    bp_method: lab?.bp_method || "",
    bp_cuff: lab?.bp_cuff_position || "",
    bp_stress: lab?.bp_stress || "",
    bp_n: lab?.bp_n_readings ?? "",
    notes: lab?.notes || "",
  });

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(initial());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lab, open, units]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const num = (v) => (v === "" || v == null ? null : parseFloat(v));
    let creat_mg = num(form.creatinine);
    if (creat_mg != null && units === "SI") creat_mg = creat_mg * CREAT_SI_TO_US;
    try {
      await api.put(`/cats/${catId}/labs/${lab.id}`, {
        date: form.date,
        creatinine_mg_dl: creat_mg,
        sdma_ug_dl: num(form.sdma),
        usg: num(form.usg),
        upc: num(form.upc),
        bp_systolic: form.bp === "" ? null : parseInt(form.bp, 10),
        bp_method: form.bp_method || null,
        bp_cuff_position: form.bp_cuff || null,
        bp_stress: form.bp_stress || null,
        bp_n_readings: form.bp_n === "" ? null : parseInt(form.bp_n, 10),
        notes: form.notes || null,
      });
      toast.success("Lab updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-lab-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit lab result</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="edit-lab-date">Date</Label>
            <Input
              id="edit-lab-date"
              type="date"
              required
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="edit-lab-date-input"
              className="mt-1.5 rounded-xl"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-lab-creat">Creatinine ({creatUnit})</Label>
              <Input
                id="edit-lab-creat"
                type="number"
                step="0.01"
                value={form.creatinine}
                onChange={(e) => setForm({ ...form, creatinine: e.target.value })}
                data-testid="edit-lab-creatinine-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="edit-lab-sdma">SDMA (µg/dL)</Label>
              <Input
                id="edit-lab-sdma"
                type="number"
                step="0.1"
                value={form.sdma}
                onChange={(e) => setForm({ ...form, sdma: e.target.value })}
                data-testid="edit-lab-sdma-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="edit-lab-usg">USG</Label>
              <Input
                id="edit-lab-usg"
                type="number"
                step="0.001"
                value={form.usg}
                onChange={(e) => setForm({ ...form, usg: e.target.value })}
                data-testid="edit-lab-usg-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="edit-lab-upc">UPC</Label>
              <Input
                id="edit-lab-upc"
                type="number"
                step="0.01"
                value={form.upc}
                onChange={(e) => setForm({ ...form, upc: e.target.value })}
                data-testid="edit-lab-upc-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="edit-lab-bp">Systolic BP (mmHg)</Label>
              <Input
                id="edit-lab-bp"
                type="number"
                value={form.bp}
                onChange={(e) => setForm({ ...form, bp: e.target.value })}
                data-testid="edit-lab-bp-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
          </div>

          {form.bp !== "" && (
            <div className="rounded-xl bg-secondary/60 p-4 space-y-3 border border-border/60">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Measurement conditions
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Method</Label>
                  <Select value={form.bp_method} onValueChange={(v) => setForm({ ...form, bp_method: v })}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="edit-lab-bp-method-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Doppler">Doppler</SelectItem>
                      <SelectItem value="Oscillometric">Oscillometric</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Cuff position</Label>
                  <Select value={form.bp_cuff} onValueChange={(v) => setForm({ ...form, bp_cuff: v })}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="edit-lab-bp-cuff-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Forelimb">Forelimb</SelectItem>
                      <SelectItem value="Hindlimb">Hindlimb</SelectItem>
                      <SelectItem value="Tail">Tail</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Stress level</Label>
                  <Select value={form.bp_stress} onValueChange={(v) => setForm({ ...form, bp_stress: v })}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="edit-lab-bp-stress-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Calm">Calm</SelectItem>
                      <SelectItem value="Anxious">Anxious</SelectItem>
                      <SelectItem value="Aggressive">Aggressive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit-lab-bp-n" className="text-xs">Avg of N readings</Label>
                  <Input
                    id="edit-lab-bp-n"
                    type="number"
                    min="1"
                    value={form.bp_n}
                    onChange={(e) => setForm({ ...form, bp_n: e.target.value })}
                    data-testid="edit-lab-bp-n-input"
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="edit-lab-notes">Notes</Label>
            <Textarea
              id="edit-lab-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="edit-lab-notes-input"
              className="mt-1.5 rounded-xl"
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} data-testid="edit-lab-submit-button" className="rounded-full">
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
